/**
 * SA-460 (workflow UX sweep spec §7.2, D5): the record's process as a
 * full-width band above the two columns — a non-collapsing `RecordSection`
 * whose header carries the Map | List control (the person's view, remembered
 * per user in `ui.records.processView`), the map's zoom buttons in map mode
 * and the primary action; the stale-fields line under it; then the map
 * (spec §4) or the timeline (§7.3). A card or an entry opens the shared
 * `StepDrawer` with its live half (§6.3, from the pure `projectStepLive`);
 * Re-open goes through the core dialog and reports `onChanged`. Every read
 * lives here (the record, its definition, its completed tasks and its
 * ledger — the last two fetched on mount because the timeline and the drawer
 * need them); the drawer and the timeline only render. Replaces
 * `RecordProcessPanel` on the lane. The open-task rows, the completed-tasks
 * disclosure and the now line moved to SA-461's Tasks section and strip
 * (spec §7.1, §7.4).
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button, Tabs, TabsList, TabsTrigger, Tip } from '@/components/ui'
import { useFeature, usePermission } from '@/hooks'
import { processErrorMessage, useRecordBinding, useRecordProcess, useRecordTasks, useReopenTask, useStartRecordProcess } from '@/hooks/useProcess'
import { useRecordEvents } from '@/hooks/useRecordExtras'
import { useDefaultTimezone } from '@/hooks/useDefaultTimezone'
import { useRecordNoun } from '@/hooks/useRecordNoun'
import { useWorkflowDefinition } from '@/hooks/useWorkflowDefinition'
import { useSettingsStore } from '@/stores/settingsStore'
import { projectProcess } from '@/utils/workflowOverview'
import type { CustomerUpdateSummary, OrderEventItem } from '@/types/commerce'
import type { TaskRead } from '@/types/process'
import type { ProcessWorkflowDefinitionJson } from '@/types/workflowDefinition'
import { ProcessMap } from '@/components/process/map/ProcessMap'
import { ReopenTaskDialog } from '@/components/process/ReopenTaskDialog'
import { StepDrawer } from '@/components/process/StepDrawer'
import { projectStepLive } from '@/components/process/stepLive'
import { fromHere, taskLinkState } from '@/components/process/taskLink'
import { bandResponseLine, bandUpdateLine, RECORD_IN_QUOTES, REVIEW_IN_INTAKE } from './customerUpdateText'
import { birthKind, birthLine, type BirthFacts } from './birthText'
import { firstOpenable, openTaskAction } from './openTaskAction'
import { useOpenPanel } from './panelLanding'
import { RecordSection } from './RecordSection'
import { RecordTimeline } from './RecordTimeline'
import type { SourceRef } from './sourceText'
import { landingTarget } from './bandLanding'
import { passView } from './passes'
import { useBandLanding } from './useBandLanding'
import { buildTimeline } from './timelineModel'
import { waitingReason, waitingSeatId, waitingSeatLabel } from './waitingSeat'
import { seatRolesTitle, waitingLine } from './waitingText'

export interface RecordProcessBandProps {
  orderId: string
  orderType: string
  isOpen: boolean
  canStart: boolean
  refreshToken?: number
  /** E4 (spec D25): where the record came from — the drawer's conversation step links it. */
  source?: SourceRef | null
  /** SA-469 (spec §3): what the page holds about the birth (the order's snapshot and its source) — step 1's drawer line and timeline row. */
  birth?: BirthFacts | null
  /** id → display name, the page's assignee map (SA-330: never render an id). */
  assigneeNames?: Record<string, string>
  /** `?step=` — the open drawer's step; the page owns the URL (spec §6.4). */
  selectedStep: string | null
  onSelectStep: (id: string | null) => void
  /** SA-1029 (spec §D22): the record's waiting customer updates, from the order read. */
  customerUpdate?: CustomerUpdateSummary | null
  /** SA-1328 (spec §4): a task was just done — bring the new current step on screen once. */
  landing?: boolean
  /** The landing started; the page clears its flag. */
  onLanded?: () => void
  /** A re-open landed: the page refetches the detail and bumps the token. */
  onChanged: () => void
}

const NO_TASKS: TaskRead[] = []
const NO_EVENTS: OrderEventItem[] = []

export function RecordProcessBand({
  orderId, orderType, isOpen, canStart, refreshToken, source = null, birth = null, assigneeNames, selectedStep, onSelectStep, onChanged, customerUpdate = null, landing = false, onLanded,
}: RecordProcessBandProps) {
  const enabled = useFeature('process_engine')
  const canWork = usePermission('tasks:work')
  const active = enabled && orderType === 'quote'
  const record = useRecordProcess(active ? orderId : undefined)
  const data = record.data ?? null
  const definition = useWorkflowDefinition(data?.workflow_id)
  const done = useRecordTasks(data ? orderId : undefined, 'done')
  const events = useRecordEvents(active ? orderId : undefined)
  const start = useStartRecordProcess()
  // SA-469 (spec D6): name what Start would start — asked only while no process exists.
  const binding = useRecordBinding(active ? orderId : undefined, record.isSuccess && data === null && isOpen && canStart)
  const reopen = useReopenTask()
  const noun = useRecordNoun().singular.toLowerCase()
  const navigate = useNavigate()
  const location = useLocation()
  const zone = useDefaultTimezone()
  // SA-1418 (A2): a second press lands the section again, never a second navigation.
  const openPanel = useOpenPanel()
  const storedView = useSettingsStore((s) => s.ui.records.processView)
  const setRecordsPrefs = useSettingsStore((s) => s.setRecordsPrefs)
  // An unknown stored value (another client's) reads as the map and is not
  // written back until the person chooses.
  const mode: 'map' | 'list' = storedView === 'list' ? 'list' : 'map'
  const [toolbarEl, setToolbarEl] = useState<HTMLDivElement | null>(null)
  const [reopening, setReopening] = useState<{ id: string; stepTitle: string } | null>(null)

  const { refetch: refetchRecord } = record
  const { refetch: refetchDone } = done
  const { refetch: refetchEvents } = events
  // SA-401: the Intake panel (hand-rolled lane) tells us a revision landed by
  // bumping this token; the reads refetch instead of sharing a client. The
  // history and the ledger are disabled until the record exists, and
  // `refetch()` ignores `enabled` (it would request `/records/undefined/…`),
  // so they refetch only with a record; the token is compared against a ref so
  // a later `data` change never re-fires the effect.
  const seenToken = useRef(refreshToken)
  useEffect(() => {
    if (refreshToken === seenToken.current) return
    seenToken.current = refreshToken
    void refetchRecord()
    if (data) {
      void refetchDone()
      void refetchEvents()
    }
  }, [refreshToken, data, refetchRecord, refetchDone, refetchEvents])

  const def = definition.data?.definition.kind === 'process' ? (definition.data.definition as ProcessWorkflowDefinitionJson) : null
  const vm = useMemo(() => (def ? projectProcess(def) : null), [def])
  const doneTasks = done.data?.tasks ?? NO_TASKS
  const eventList = events.data ?? NO_EVENTS
  // SA-469 (spec D8): the birth code Start stamped on the entry step's completion.
  const entryVia = useMemo(
    () => (def ? eventList.find((e) => e.eventType === 'process_step_completed' && e.payload?.step === def.entry)?.payload?.via : undefined),
    [def, eventList],
  )
  const drawerBirth = useMemo(() => {
    if (!birth) return null
    const kind = birthKind(entryVia, birth.sourceKind)
    return { line: kind ? birthLine(kind, birth, assigneeNames) : null }
  }, [birth, entryVia, assigneeNames])
  const live = useMemo(
    () => (data && vm && def && selectedStep
      ? projectStepLive(data, doneTasks, eventList, selectedStep, { vm, definition: def, assigneeNames })
      : null),
    [data, vm, def, selectedStep, doneTasks, eventList, assigneeNames],
  )
  const timeline = useMemo(
    () => (data && vm && def && mode === 'list' ? passView(buildTimeline(data, doneTasks, eventList, { vm, definition: def, sourceKind: birth?.sourceKind ?? null, zone })) : null),
    [data, vm, def, mode, doneTasks, eventList, birth, zone],
  )
  const bandRef = useRef<HTMLElement | null>(null)
  const drawnSteps = useMemo(() => new Set(vm?.steps.map((st) => st.id) ?? []), [vm])
  const target = useMemo(() => (data ? landingTarget(data, mode, timeline, drawnSteps) : null), [data, mode, timeline, drawnSteps])
  const { ringStep, ringKey } = useBandLanding({
    active: landing, ready: data !== null && (mode === 'map' ? vm !== null : timeline !== null), target, rootRef: bandRef, onLanded,
  })
  if (!active) return null

  // SA-480: the first open task THIS viewer can open; SA-1077 (D2): what
  // opening it does is `openTaskAction`, shared with the Tasks section's Current task block. A
  // task on a seat they do not hold reads as Waiting on that task's OWN
  // stamped seat (F3 of the final review) with the reason (`waitingReason`)
  // and the seat's bound roles in the title, from the live definition.
  const firstOpen = firstOpenable(data)
  const action = firstOpen ? openTaskAction(firstOpen, data) : null
  const waitingSeat = waitingSeatLabel(data)
  const reason = waitingReason(data, canWork)
  const seatId = waitingSeatId(data)
  const rolesTitle = waitingSeat ? seatRolesTitle(waitingSeat, def ? (seatId ? def.seats[seatId]?.roles ?? [] : []) : null) : null
  const primary = record.isSuccess && data === null
    ? (isOpen && canStart
        ? <Button size="sm" disabled={start.isPending} onClick={() => start.mutate({ orderId })}>{binding.data ? `Start ${binding.data.workflow_name}` : 'Start process'}</Button>
        : null)
    : data?.status === 'running'
      ? action
        ? action.kind === 'panel'
          ? <Button size="sm" onClick={() => openPanel(action.panel)}>{action.label}</Button>
          : <Button size="sm" onClick={() => navigate(action.to, { state: taskLinkState(fromHere(location)) })}>{action.label}</Button>
        : waitingSeat
          ? (rolesTitle
              // A disabled basis button takes no pointer or focus, so the tip rides a focusable wrapper (SA-1034 PR 5).
              // SA-1418 (A5): the wrapper is named by the waiting line (a group may take a label; a bare span
              // may not), described by the roles through the one Tip, and rings like every other control.
              ? <Tip text={rolesTitle}>
                  <span role="group" aria-label={waitingLine(waitingSeat, reason)} tabIndex={0} data-testid="waiting-roles"
                    className="rounded-md focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50">
                    <Button size="sm" variant="outline" disabled>{waitingLine(waitingSeat, reason)}</Button>
                  </span>
                </Tip>
              : <Button size="sm" variant="outline" disabled>{waitingLine(waitingSeat, reason)}</Button>)
          : null
      : null
  const controls = (
    <>
      {data && (
        <Tabs value={mode} onValueChange={(v) => setRecordsPrefs({ processView: v === 'list' ? 'list' : 'map' })}>
          <TabsList aria-label="Process view" className="h-7 p-[2px]">
            <TabsTrigger value="map" className="h-6 px-2.5 text-xs">Map</TabsTrigger>
            <TabsTrigger value="list" className="h-6 px-2.5 text-xs">List</TabsTrigger>
          </TabsList>
        </Tabs>
      )}
      {data && mode === 'map' && <div ref={setToolbarEl} data-testid="process-band-toolbar" className="flex items-center gap-1" />}
      {primary}
    </>
  )

  return (
    <>
      <RecordSection id="process" sectionRef={bandRef} tour="records.record.process" title={data ? `Process · ${data.workflow_name}` : 'Process'} collapsible={false} wrapHeader actions={controls}>
        {record.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {record.isError && (
          <div className="space-y-2">
            <p className="text-sm text-destructive">{processErrorMessage(record.error)}</p>
            <Button variant="outline" size="sm" onClick={() => record.refetch()}>Retry</Button>
          </div>
        )}
        {record.isSuccess && data === null && (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Nothing is running on this {noun}.</p>
            {start.error && <p className="text-sm text-destructive">{processErrorMessage(start.error)}</p>}
          </div>
        )}
        {data && (
          <div className="space-y-3 text-sm">
            {customerUpdate && customerUpdate.waiting > 0 && (
              <p data-testid="process-band-update" className="text-xs text-warning-strong">
                {bandUpdateLine(customerUpdate.waiting)} ·{' '}
                <button type="button" className="underline underline-offset-2"
                  onClick={() => openPanel('intake')}>
                  {REVIEW_IN_INTAKE}
                </button>
              </p>
            )}
            {customerUpdate && customerUpdate.responses > 0 && (
              <p data-testid="process-band-response" className="text-xs text-warning-strong">
                {bandResponseLine(customerUpdate.responses)} ·{' '}
                <button type="button" className="underline underline-offset-2"
                  onClick={() => openPanel('quotes')}>
                  {RECORD_IN_QUOTES}
                </button>
              </p>
            )}
            {data.stale_fields.length > 0 && (
              <p className="text-xs text-warning-strong">Values from before the latest revision or re-open: {data.stale_fields.join(', ')}</p>
            )}
            {vm && mode === 'map' && (
              <ProcessMap vm={vm} currentStep={data.current_step?.id ?? null} path={data.path} onSelect={onSelectStep} toolbarTarget={toolbarEl} ringStep={ringStep} />
            )}
            {timeline && mode === 'list' && <RecordTimeline view={timeline} ringKey={ringKey} onSelect={onSelectStep} nowAction={action} onOpenPanel={openPanel} />}
            {definition.isError && <p className="text-xs text-destructive">The workflow definition could not be loaded.</p>}
          </div>
        )}
      </RecordSection>
      {vm && def && (
        <StepDrawer
          vm={vm} definition={def} stepId={selectedStep} onClose={() => onSelectStep(null)} noun={noun} live={live} source={source}
          birth={drawerBirth} collapsibleFacts
          onReopen={(t) => { reopen.reset(); setReopening(t) }}
        />
      )}
      {reopening && (
        <ReopenTaskDialog
          stepTitle={reopening.stepTitle} busy={reopen.isPending} error={reopen.error ? processErrorMessage(reopen.error) : null}
          onClose={() => setReopening(null)}
          onConfirm={(reason) => reopen.mutate({ taskId: reopening.id, reason }, { onSuccess: () => { setReopening(null); onChanged() } })}
        />
      )}
    </>
  )
}
